import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download, Filter, Plus } from 'lucide-react';
import Button from '../components/atoms/Button';
import Loading from '../components/atoms/Loading';
import { fetchQueries } from '../redux/querymanagement/actions';
import { fetchStatuses } from '../redux/status/actions';
import { fetchWorkflowSteps } from '../redux/Workflow/actions';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { setId } from '../redux/appDetails/reducer';
import { resetQueryManagementState } from '../redux/querymanagement/reducers';
import { resetStatusState } from '../redux/status/reducers';

const QUERY_TYPE_STORAGE_KEY = 'aira-query-complaintTypeId';
const labelFieldId = 44;
const statusOptions = ['Open', 'Closed'];
const escalationOptions = ['Level 1', 'Level 2', 'Level 3'];
const STATUS_FIELD = { id: 'status', label: 'Status', key: 'status' };
const ESCALATION_FIELD = { id: 'escalationTo', label: 'Escalation To', key: 'escalationTo' };

function resolveComplaintTypeId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value.data ?? value.id ?? value._id ?? null;
  return value;
}

function readStoredComplaintTypeId() {
  try {
    return sessionStorage.getItem(QUERY_TYPE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistComplaintTypeId(value) {
  const id = resolveComplaintTypeId(value);
  if (id == null || id === '') return null;
  const normalized = String(id);
  try {
    sessionStorage.setItem(QUERY_TYPE_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage failures (private mode / disabled storage).
  }
  return normalized;
}

function firstComplaintTypeId(...values) {
  for (const value of values) {
    const resolved = resolveComplaintTypeId(value);
    if (resolved != null && resolved !== '') return String(resolved);
  }
  return null;
}

function resolveStatusLabel(complaint, statusList = []) {
  const raw = complaint?.status;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (raw && typeof raw === 'object') {
    const name = raw.name ?? raw.label ?? raw.status;
    if (name) return String(name).trim();
    const nestedId = raw.id ?? raw.statusId;
    if (nestedId != null) {
      const match = statusList.find((item) => String(item.id) === String(nestedId));
      if (match?.name) return match.name;
    }
  }
  const statusId = complaint?.statusId ?? complaint?.complaintStatusId;
  if (statusId != null) {
    const match = statusList.find((item) => String(item.id) === String(statusId));
    if (match?.name) return match.name;
  }
  return '';
}

function resolveEscalationLabel(complaint, steps = []) {
  const level = complaint?.level ?? complaint?.levelNo;
  if (level != null && level !== '') return `Level ${level}`;
  const stepId = complaint?.workflowStepId ?? complaint?.currentWorkflowStepId;
  if (stepId != null) {
    const match = steps.find((step) => String(step.id) === String(stepId));
    if (match?.levelNo != null) return `Level ${match.levelNo}`;
  }
  return '';
}

function mapComplaintRow(complaint, statusList = [], steps = []) {
  const row = {
    id: complaint?.complaintId,
    status: resolveStatusLabel(complaint, statusList),
    escalationTo: resolveEscalationLabel(complaint, steps),
  };
  (complaint?.formField || []).forEach((field) => {
    const key = field.fieldKey || field.fieldName;
    if (!key || key === 'status' || key === 'escalationTo' || key === 'id') return;
    row[key] = field.value;
  });
  return row;
}

function cellClass(key, isHeader = false) {
  const classes = [];
  if (!isHeader && key === 'description') classes.push('ca-query-table__desc');
  if (key === 'status') classes.push('ca-query-table__sticky', 'ca-query-table__sticky--status');
  if (key === 'escalationTo') classes.push('ca-query-table__sticky', 'ca-query-table__sticky--escalation');
  return classes.length ? classes.join(' ') : undefined;
}

function QuerySelect({ value, options, fallbackOptions, ariaLabel, selectKey }) {
  const names = options.length ? options : fallbackOptions;
  const selected = value && value !== '-' ? value : '';
  const list = selected && !names.includes(selected) ? [selected, ...names] : names;
  const shown = list.includes(selected) ? selected : (list[0] ?? '');

  return (
    <select
      className="ca-query-table__select"
      aria-label={ariaLabel}
      value={shown}
      onChange={() => undefined}
      key={selectKey}
    >
      {list.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </select>
  );
}

function renderCell(field, row, statusNames = statusOptions, escalationLabels = escalationOptions) {
  const value = row[field.key] ?? '-';

  if (field.key === 'status') {
    return (
      <QuerySelect
        value={value}
        options={statusNames}
        fallbackOptions={statusOptions}
        ariaLabel="Status"
        selectKey={`status-${row.id}`}
      />
    );
  }

  if (field.key === 'escalationTo') {
    return (
      <QuerySelect
        value={value}
        options={escalationLabels}
        fallbackOptions={escalationOptions}
        ariaLabel="Escalation to"
        selectKey={`escalation-${row.id}`}
      />
    );
  }

  return value;
}

export default function QueryTable({ id = labelFieldId }) {
  const dispatch = useDispatch();
  const { complaintTypeId: routeComplaintTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const storeComplaintTypeId = useSelector((store) => store.appDetails?.id);
  const complaintTypeId = firstComplaintTypeId(
    routeComplaintTypeId,
    searchParams.get('complaintTypeId'),
    storeComplaintTypeId,
    readStoredComplaintTypeId(),
  );
  const {
    list = [],
    isLoading = false,
    error = null,
    hasLoaded = false,
    requestedComplaintTypeId = null,
  } = useSelector((store) => store.queryManagement ?? {});
  const statusList = useSelector((store) => store.status?.list ?? []);
  const statusNames = useMemo(
    () => statusList.map((item) => item.name).filter(Boolean),
    [statusList],
  );
  const workflowSteps = useSelector((store) => store.workflow?.workflowSteps);
  const escalationLabels = useMemo(() => {
    const steps = Array.isArray(workflowSteps) ? workflowSteps : [];
    return [...steps]
      .sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0))
      .map((step) => (step.levelNo != null ? `Level ${step.levelNo}` : ''))
      .filter(Boolean);
  }, [workflowSteps]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!complaintTypeId || routeComplaintTypeId) return undefined;
    persistComplaintTypeId(complaintTypeId);
    navigate(`/querymanagement/${complaintTypeId}`, { replace: true });
    return undefined;
  }, [complaintTypeId, navigate, routeComplaintTypeId]);

  useEffect(() => {
    if (!complaintTypeId) return undefined;
    if (!routeComplaintTypeId) return undefined;

    persistComplaintTypeId(complaintTypeId);
    dispatch(setId(complaintTypeId));
    dispatch(resetQueryManagementState());
    dispatch(resetStatusState());

    const queriesRequest = dispatch(fetchQueries({
      complaintTypeId,
      page: 1,
      limit: 10,
    }));
    const statusesRequest = dispatch(fetchStatuses(complaintTypeId));
    const workflowRequest = dispatch(fetchWorkflowSteps(complaintTypeId));

    return () => {
      queriesRequest.abort?.();
      statusesRequest.abort?.();
      workflowRequest.abort?.();
    };
  }, [complaintTypeId, dispatch, routeComplaintTypeId]);

  const mappedRows = useMemo(() => {
    if (
      requestedComplaintTypeId != null &&
      complaintTypeId != null &&
      String(requestedComplaintTypeId) !== String(complaintTypeId)
    ) {
      return [];
    }
    return list.map((complaint) => ({
      ...mapComplaintRow(
        complaint,
        statusList,
        Array.isArray(workflowSteps) ? workflowSteps : [],
      ),
      _fields: complaint?.formField || [],
    }));
  }, [complaintTypeId, list, requestedComplaintTypeId, statusList, workflowSteps]);

  const apiColumns = useMemo(() => {
    const seen = new Set();
    const columns = [];
    mappedRows.forEach((row) => {
      (row._fields || []).forEach((field) => {
        const key = field.fieldKey || field.fieldName;
        if (!key || key === 'status' || key === 'escalationTo' || seen.has(key)) return;
        seen.add(key);
        columns.push({
          id: key,
          label: field.fieldName || key,
          key,
        });
      });
    });
    return columns;
  }, [mappedRows]);

  const resolvedFields = [...apiColumns, STATUS_FIELD, ESCALATION_FIELD];
  const resolvedRows = mappedRows.map(({ _fields, ...row }) => row);
  const scrollFields = resolvedFields.filter((field) => field.key !== 'status' && field.key !== 'escalationTo');
  const stickyFields = [STATUS_FIELD, ESCALATION_FIELD];
  const showLoading = Boolean(
    complaintTypeId &&
    !error &&
    !resolvedRows.length &&
    (isLoading || !hasLoaded)
  );

  const handleNew =()=>{
    if (!complaintTypeId) return;
    navigate(`/querymanagement/new/${complaintTypeId}`);
  };
  return (
    <section className="ca-query-table" aria-label="Query records">
      <div className="ca-query-table__toolbar">
        <label className="ca-query-table__filter">
          <Filter size={14} aria-hidden="true" />
          <span className="ca-query-table__sr-only">Filter by status</span>
          <select defaultValue="all" aria-label="Filter by status">
            <option value="all">Select Status</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <div className="ca-query-table__actions">
          <Button variant="outline" onClick={() => handleNew()} size="sm" startIcon={<Plus size={14} />}>
         
            New Query
        
          </Button>
          <Button variant="outline" size="sm" startIcon={<Download size={14} />}>
            Download Report
          </Button>
        </div>
      </div>

      {error ? <p className="ca-body">{error}</p> : null}
      {showLoading ? (
        <Loading text="Loading queries" />
      ) : (
      <div className="ca-query-table__scroll" data-label-field-id={id}>
        <table>
          <thead>
            <tr>
              <th scope="col">#</th>
              {scrollFields.map((field) => (
                <th scope="col" key={field.id} className={cellClass(field.key, true)}>{field.label}</th>
              ))}
              <th className="ca-query-table__spacer" aria-hidden="true" />
              {stickyFields.map((field) => (
                <th scope="col" key={field.id} className={cellClass(field.key, true)}>{field.label}</th>
              ))}
              {/* <th className="ca-query-table__sticky-column ca-query-table__sticky-column--status" scope="col">{statusField.label}</th> */}
              {/* <th className="ca-query-table__sticky-column ca-query-table__sticky-column--escalation" scope="col">{escalationField.label}</th> */}
            </tr>
          </thead>
          <tbody>
            {resolvedRows.map((row, index) => (
              <tr key={row.id ?? index}>
                <td>{index + 1}</td>
                {scrollFields.map((field) => (
                  <td
                    key={`${row.id ?? index}-${field.id}`}
                    className={cellClass(field.key)}
                  >
                    {renderCell(field, row, statusNames, escalationLabels)}
                  </td>
                ))}
                <td className="ca-query-table__spacer" aria-hidden="true" />
                {stickyFields.map((field) => (
                  <td
                    key={`${row.id ?? index}-${field.id}`}
                    className={cellClass(field.key)}
                  >
                    {renderCell(field, row, statusNames, escalationLabels)}
                  </td>
                ))}
                {/* <td className="ca-query-table__sticky-column ca-query-table__sticky-column--status">
                  <span className="ca-query-table__select-value">{row[statusField.key] ?? '-'}</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </td>
                <td className="ca-query-table__sticky-column ca-query-table__sticky-column--escalation">
                  <span className="ca-query-table__select-value">{row[escalationField.key] ?? '-'}</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}
